import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry } from '../utils/countries';
import './Dashboard.css';

function MyPayments({ user, onLogout }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/payment/my-payments');
      setPayments(res.data.payments);
    } catch (err) {
      setError(t('error.loadingData') || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { className: 'success', label: t('payment.statusCompleted') || 'Başarılı' };
      case 'pending':
        return { className: 'warning', label: t('payment.statusPending') || 'Beklemede' };
      case 'failed':
        return { className: 'danger', label: t('payment.statusFailed') || 'Başarısız' };
      case 'refunded':
        return { className: 'info', label: t('payment.statusRefunded') || 'İade' };
      default:
        return { className: 'secondary', label: status };
    }
  };

  const formatAmount = (amount, currency) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    const symbol = currency === 'USD' ? '$' : currency === 'TRY' ? '₺' : currency;
    return `${symbol}${num.toFixed(2)}`;
  };

  // Toplam istatistikler
  const stats = {
    total: payments.length,
    completed: payments.filter(p => p.status === 'completed').length,
    pending: payments.filter(p => p.status === 'pending').length,
    totalSpentUsd: payments
      .filter(p => p.status === 'completed' && p.currency === 'USD')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    totalSpentTry: payments
      .filter(p => p.status === 'completed' && p.currency === 'TRY')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
  };

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <h1>💳 {t('nav.myPayments') || 'Ödemelerim'}</h1>
          <p>{t('payment.historyDescription') || 'Ödeme geçmişinizi ve işlem detaylarınızı görüntüleyin.'}</p>
        </header>

        {/* İstatistik Kartları */}
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <h3>{t('payment.totalPayments') || 'Toplam İşlem'}</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card success">
            <h3>{t('payment.successfulPayments') || 'Başarılı'}</h3>
            <p className="stat-number">{stats.completed}</p>
          </div>
          {stats.totalSpentUsd > 0 && (
            <div className="stat-card info">
              <h3>{t('payment.totalSpent') || 'Toplam Harcama'} (USD)</h3>
              <p className="stat-number">${stats.totalSpentUsd.toFixed(2)}</p>
            </div>
          )}
          {stats.totalSpentTry > 0 && (
            <div className="stat-card info">
              <h3>{t('payment.totalSpent') || 'Toplam Harcama'} (TRY)</h3>
              <p className="stat-number">₺{stats.totalSpentTry.toFixed(2)}</p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading">{t('loading.text') || 'Yükleniyor...'}</div>
        ) : error ? (
          <div className="payment-error" style={{ padding: '16px', background: '#fef2f2', borderRadius: '8px', color: '#ef4444' }}>
            ⚠️ {error}
          </div>
        ) : payments.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💳</div>
            <h3 style={{ margin: '0 0 8px', color: '#374151' }}>{t('payment.noPayments') || 'Henüz ödeme yapılmamış'}</h3>
            <p>{t('payment.noPaymentsDesc') || 'Ödeme yaptığınızda işlem geçmişiniz burada görünecektir.'}</p>
          </div>
        ) : (
          <section className="section">
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('payment.date') || 'Date'}</th>
                    <th>{t('payment.domain') || 'Domain'}</th>
                    <th>{t('payment.duration') || 'Duration'}</th>
                    <th>{t('payment.amountDue') || 'Amount'}</th>
                    <th>{t('payment.currency') || 'Currency'}</th>
                    <th>{t('payment.status') || 'Status'}</th>
                    <th>{t('payment.orderNo') || 'Order No'}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => {
                    const badge = getStatusBadge(payment.status);
                    return (
                      <tr key={payment.id} style={payment.status === 'failed' ? { opacity: 0.6 } : {}}>
                        <td data-label={t('payment.date') || 'Date'}>{formatDateForCountry(payment.created_at, user?.country)}</td>
                        <td data-label={t('payment.domain') || 'Domain'}>
                          <a href={`${payment.domain_url}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontWeight: 500 }}>
                            {payment.domain_url}
                          </a>
                        </td>
                        <td data-label={t('payment.duration') || 'Duration'}>
                          {payment.months
                            ? `${payment.months} ${t('payment.months') || 'Ay'}`
                            : '-'
                          }
                        </td>
                        <td style={{ fontWeight: 600 }} data-label={t('payment.amountDue') || 'Amount'}>
                          {formatAmount(payment.amount, payment.currency)}
                        </td>
                        <td data-label={t('payment.currency') || 'Currency'}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.8rem', color: '#6b7280'
                          }}>
                            {payment.currency === 'TRY' ? '🇹🇷' : payment.currency === 'USD' ? '🇺🇸' : ''}
                            {payment.currency}
                          </span>
                        </td>
                        <td data-label={t('payment.status') || 'Status'}>
                          <span className={`badge ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td data-label={t('payment.orderNo') || 'Order No'}>
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {payment.merchant_oid || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      <style>{`
        .badge.danger {
          background-color: #fef2f2;
          color: #ef4444;
        }
        .badge.info {
          background-color: #eff6ff;
          color: #3b82f6;
        }
        .empty-state {
          background: #f9fafb;
          border-radius: 12px;
          border: 2px dashed #e5e7eb;
        }
      `}</style>
    </div>
  );
}

export default MyPayments;
